import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import { NavBar } from "../navbar/navbar";
import { useApi } from "../home/apiService";
import { useLanguage } from "../../i18n/LanguageProvider";
import { refreshSession } from "../../auth/authBridge";
import { invalidatePlanStatusCache } from "../../hooks/usePlanStatus";
import "../../styles/sp-ui.css";

// Stripe Checkout / Portal return page (success_url, cancel_url & the portal
// return_url point here).
//
// Only a checkout success (?status=success) runs the activation flow: the plan
// flips once Stripe's state reaches the server, so we ask the server to pull it
// directly (POST /billing/sync) — which normally beats the webhook — then
// refresh the access token (picking up the bumped permission_version), and fall
// back to polling the plan endpoint until the subscription reads "active". If
// it hasn't landed in time we say so — the existing silent refresh will pick it
// up on the next request regardless.
//
// A portal return (?status=portal, or no params) didn't necessarily buy
// anything, so it must not see the success message: we sync silently so any
// changes made in the portal (e.g. a cancellation) take effect, then bounce
// straight back to /account.

type Phase = "activating" | "active" | "pending" | "cancelled" | "syncing";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10; // ~20s

export const BillingReturn = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const location = useLocation();
  const status = new URLSearchParams(location.search).get("status");

  const [phase, setPhase] = useState<Phase>(
    status === "cancelled"
      ? "cancelled"
      : status === "success"
        ? "activating"
        : "syncing",
  );
  const cancelled = useRef(false);

  useEffect(() => {
    if (status === "cancelled") return;
    cancelled.current = false;

    if (status !== "success") {
      // Portal return (or direct navigation): sync so portal changes land,
      // then leave quietly — no purchase happened, so no success message.
      const syncAndLeave = async () => {
        try {
          await api.syncBilling();
        } catch {
          // Fail open: the silent refresh picks it up on the next request.
        }
        if (cancelled.current) return;
        await refreshSession();
        if (!cancelled.current) navigate("/account", { replace: true });
      };
      syncAndLeave();
      return () => {
        cancelled.current = true;
      };
    }

    const poll = async () => {
      // Ask the server to sync from Stripe directly — usually resolves the
      // subscription before the webhook does. Errors fall through to polling.
      let synced: string | null = null;
      try {
        synced = (await api.syncBilling()).subscriptionStatus;
      } catch {
        // Fail open: the poll loop below covers us.
      }
      if (cancelled.current) return;
      // Refresh after the sync so the new token carries the bumped
      // permission_version from a plan flip.
      await refreshSession();
      if (cancelled.current) return;
      if (synced === "active") {
        invalidatePlanStatusCache();
        setPhase("active");
        return;
      }
      for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
        if (cancelled.current) return;
        try {
          const plan = await api.fetchMyPlan();
          if (plan.subscriptionStatus === "active") {
            invalidatePlanStatusCache();
            if (!cancelled.current) setPhase("active");
            return;
          }
        } catch {
          // Ignore transient errors and keep polling.
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      if (!cancelled.current) setPhase("pending");
    };

    poll();
    return () => {
      cancelled.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const titleKey =
    phase === "activating"
      ? "billing.activatingTitle"
      : phase === "active"
        ? "billing.successTitle"
        : phase === "pending"
          ? "billing.pendingTitle"
          : "billing.cancelledTitle";
  const bodyKey =
    phase === "activating"
      ? "billing.activatingBody"
      : phase === "active"
        ? "billing.successBody"
        : phase === "pending"
          ? "billing.pendingBody"
          : "billing.cancelledBody";

  return (
    <>
      <NavBar />
      <Container fluid className="sp-page">
        <div className="sp-card" style={{ maxWidth: 520, margin: "48px auto", textAlign: "center" }}>
          {(phase === "activating" || phase === "syncing") && (
            <span className="sp-spinner" style={{ margin: "0 auto 16px" }} />
          )}
          {phase !== "syncing" && (
            <>
              <h2 className="sp-card__title" style={{ marginBottom: 8 }}>
                {t(titleKey)}
              </h2>
              <p className="sp-page__sub" style={{ marginBottom: 20 }}>
                {t(bodyKey)}
              </p>
            </>
          )}
          {phase !== "activating" && phase !== "syncing" && (
            <button
              className="sp-btn sp-btn--primary"
              onClick={() => navigate("/account")}
            >
              {t("billing.backToAccount")}
            </button>
          )}
        </div>
      </Container>
    </>
  );
};
